import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Collapse, InputNumber, List, Modal, Select, Space, Tag, Typography } from 'antd';

import { InputSelect } from '../Input';
import { buildCardHierarchyOptions } from './CardVisuals';
import { classify, transformInfo } from '../../config/elementCoercion';
import DataTypeBadge from '../common/DataTypeBadge';
import { normalizeId } from '../../utils/ids';
import { createElementResolver, indexCardsById } from '../../utils/cards';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { buildFilterFields } from '../../utils/kqlBuilder';
import { cardAndDescendantIds } from '../../utils/facets';
import {
	COUNT_SOURCE_ELEMENT,
	ENTRY_JOINER,
	SYSTEM_SOURCE_LABELS,
	WINDOW_SYSTEM_LABELS,
	describeDrivenRule,
	drivenFilterProblem,
	elementLabel,
	entryRefOf,
	groupNamed,
	linkLabelFor,
	linkOptionsFor,
	targetCardChain,
} from '../../utils/drivenSummary';
import { columnsOf } from '../../config/repeatingGroups';
import DrivenWhereEditor from './DrivenWhereEditor';
import DrivenTryIt from './DrivenTryIt';

/**
 * Configures ONE driven element: "this element's value comes from the items
 * of another card that link to this one". The user edits `options.drivenFrom`
 * on the TARGET element; the API materializes a managed rule from it on card
 * save.
 *
 * THE DIALOG READS AS ONE SENTENCE the person completes top to bottom (KPR-950,
 * KPE-010; docs/platform-standards.md §15, "Setup is picked, not typed"):
 * From [card] items, linked by [lookup]; Take [the count | the total …] (of
 * [element], only when the aggregate reads one); Only those where [conditions];
 * Over [all time | the last n days by a date]. Every blank is a select, a pill
 * or a number. Underneath, the whole rule in words (utils/drivenSummary.js) —
 * the same sentence the card's Elements tab shows.
 *
 * THE STORED SHAPE DOES NOT CHANGE. `where.filter` is still KQL naming the
 * source card's elements by `#id`; the condition builder writes it (and the
 * API re-validates it), and the text is one click away behind Edit as text
 * (DrivenWhereEditor). A count still stores `$createdAt` as its source element:
 * a count reads no element, so the dialog asks for none.
 *
 * Why progressive sections rather than a wizard: every later choice depends on
 * an earlier one (which elements exist depends on the source card; which
 * conversions are legal depends on the element pair), so sections appear as
 * their inputs become answerable — but nothing already answered is ever hidden
 * behind a step the user has to walk back through.
 *
 * The element list is sorted by the coercion matrix mirrored from the API
 * (config/elementCoercion.js): the elements that fit first, those that convert
 * after them (badged), and those the engine would refuse behind "Show n that
 * don't fit", each with its reason in words.
 *
 * The write-through warning near the bottom is REQUIRED, not decorative. A
 * driven element is the one sanctioned place where a user's edit writes onto
 * items they may not be able to edit (or even read); that delegation is made
 * here, by a collection manager, so the consequence is stated here.
 */

const SYSTEM_ELEMENTS = [
	{ name: '$createdAt', label: SYSTEM_SOURCE_LABELS.$createdAt, dataType: 'date-time', system: true },
	{ name: '$updatedAt', label: SYSTEM_SOURCE_LABELS.$updatedAt, dataType: 'date-time', system: true },
];

const DATE_TYPES = ['date', 'date-time'];

const MATCH_CLASS_META = {
	exact: { rank: 0, tag: null },
	coercible: { rank: 1, tag: 'converts' },
	unmappable: { rank: 2, tag: 'can’t be used' },
};

// Exported so an automatic tag's related clause (components/tags/
// TagRuleClause.js) names an aggregate exactly the way a driven element does.
export const AGGREGATE_OPTIONS = [
	{ value: 'latest', label: 'Latest value' },
	{ value: 'count', label: 'Count of items' },
	{ value: 'sum', label: 'Sum of values' },
	{ value: 'min', label: 'Smallest value' },
	{ value: 'max', label: 'Largest value' },
	{ value: 'average', label: 'Average value' },
];

// A window's units, shared with the automatic tags' clause for the same reason.
export const WINDOW_UNIT_OPTIONS = [
	{ value: 'minute', label: 'minutes' },
	{ value: 'hour', label: 'hours' },
	{ value: 'day', label: 'days' },
	{ value: 'week', label: 'weeks' },
];

// The time window is ONE choice: all of them, or the last so many.
const WINDOW_SPAN_OPTIONS = [
	{ value: 'all', label: 'All time' },
	{ value: 'last', label: 'The last…' },
];

// The numeric aggregates need a number or measurement source; they keep the
// source's own type (a measurement sum is a measurement, over its base).
const NUMERIC_AGGREGATES = new Set(['sum', 'min', 'max', 'average']);
const NUMERIC_SOURCE_TYPES = new Set(['number', 'decimal', 'integer', 'rating', 'measurement']);

// Exported so the read-side rule summary (components/items/DrivenRuleModal.js)
// names a transform exactly the way the authoring modal named it — one label
// per transform, in one place.
export const TRANSFORM_LABELS = {
	none: 'Use the value as-is',
	toText: 'Convert to text',
	truncate: 'Cut off at 255 characters',
	stripFormatting: 'Strip rich-text formatting',
	dateToText: 'Convert the date to text',
	dateTimeToDate: 'Drop the time of day',
	dateToDateTime: 'Add a midnight time of day',
	numberToDate: 'Read the number as epoch milliseconds',
	numberToDateTime: 'Read the number as epoch milliseconds',
	dateToNumber: 'Convert the date to epoch milliseconds',
	// "Allow multiple" on a Choice (data types PR 5): a list into one choice,
	// one choice into a list, a list into text.
	firstChoice: 'Keep the first choice',
	wrapChoice: 'Use the choice as a list of one',
	joinChoices: 'Join the choices into text',
};

const aggregateLabel = value => AGGREGATE_OPTIONS.find(option => option.value === value)?.label || value;

// Across a repeating group's entries (KPR-1055) a count counts ENTRIES, and
// the picker says so; every other aggregate keeps its name.
const ENTRY_AGGREGATE_OPTIONS = AGGREGATE_OPTIONS.map(option => (option.value === 'count' ? { ...option, label: 'Count of entries' } : option));
// A sum and an average take one value PER ENTRY that names this item, so they
// read a column of the group (the API refuses an element of the record).
const PER_ENTRY_AGGREGATES = new Set(['sum', 'average']);

export default function DrivenFromModal({ open, onCancel, onSave, element, targetCard, collectionCards = [], collectionId = null }) {

	// The app-wide cache resolves inherited elements; it is OPTIONAL so this
	// modal still renders standalone (tests, and the card form before the cache
	// has settled). Falling back to a resolver built from the cards we were
	// handed keeps the same inheritance rules either way.
	const cardDefinitions = useOptionalCardDefinitions();
	const elementSetsById = cardDefinitions?.elementSetsById || null;
	const localResolver = useMemo(
		() => createElementResolver(indexCardsById(collectionCards), elementSetsById),
		[collectionCards, elementSetsById]
	);
	const getElementsForCard = cardDefinitions?.getElementsForCard || localResolver;
	// Every card the modal can see, for the cards beneath the source.
	const cardsByIdForIds = useMemo(
		() => (cardDefinitions?.cards?.length ? indexCardsById(cardDefinitions.cards) : indexCardsById(collectionCards)),
		[cardDefinitions?.cards, collectionCards]
	);

	const initialConfig = element?.options?.drivenFrom || null;
	const targetType = element?.dataType;
	const targetOptions = element?.options;
	const targetCardName = targetCard?.name || 'this card';

	const [sourceCardId, setSourceCardId] = useState('');
	const [sourceElementName, setSourceElementName] = useState('');
	const [linkElementName, setLinkElementName] = useState('');
	const [aggregate, setAggregate] = useState('latest');
	// WHICH of the linked items the aggregate reads. Without this every
	// related aggregate is a lifetime total, which is the exception in real
	// reporting: "doses today", "hours this week", "spend year to date" and
	// "open tickets" are the same question with a window and a filter on it.
	const [windowField, setWindowField] = useState('');
	const [windowEvery, setWindowEvery] = useState(7);
	const [windowUnit, setWindowUnit] = useState('day');
	const [whereFilter, setWhereFilter] = useState('');
	const [whereProblem, setWhereProblem] = useState(null);
	const [showUnfit, setShowUnfit] = useState(false);
	const [orderBy, setOrderBy] = useState('');
	const [transformType, setTransformType] = useState('');
	// A stored transform survives the first render, where the source card isn't
	// resolved yet and the matrix therefore offers nothing: without this the
	// legality pass below would quietly demote a saved second-choice transform
	// to the pair's default on every reopen.
	const seededTransformRef = useRef('');

	// Opening the modal (re)seeds it from whatever is already stored on the
	// element — Cancel must be able to discard edits, so state is never read
	// straight from the config while the modal is open.
	useEffect(() => {
		if (!open) return;
		setSourceCardId(normalizeId(initialConfig?.sourceCardId) || '');
		setSourceElementName(initialConfig?.sourceElementName || (initialConfig?.aggregate === 'count' ? COUNT_SOURCE_ELEMENT : ''));
		setLinkElementName(initialConfig?.linkElementName || '');
		setAggregate(initialConfig?.aggregate || 'latest');
		setWindowField(initialConfig?.where?.window?.field || '');
		setWindowEvery(initialConfig?.where?.window?.every ?? 7);
		setWindowUnit(initialConfig?.where?.window?.unit || 'day');
		setWhereFilter(initialConfig?.where?.filter || '');
		setWhereProblem(null);
		setShowUnfit(false);
		setOrderBy(initialConfig?.orderBy || '');
		setTransformType(initialConfig?.transform?.type || '');
		seededTransformRef.current = initialConfig?.transform?.type || '';
	}, [open, initialConfig]);

	const cardOptions = useMemo(() => buildCardHierarchyOptions(collectionCards), [collectionCards]);

	const sourceCard = useMemo(
		() => (collectionCards || []).find(card => normalizeId(card?._id) === sourceCardId) || null,
		[collectionCards, sourceCardId]
	);
	const sourceCardName = sourceCard?.name || 'the source card';

	const sourceElements = useMemo(
		() => (sourceCardId ? (getElementsForCard(sourceCardId) || []) : []),
		[getElementsForCard, sourceCardId]
	);
	// What an id the filter holds names: the source card AND the cards
	// beneath it, which a driven total counts too (the P4 review's R-WL1).
	const sourceIdFields = useMemo(() => {
		const below = sourceCardId ? cardAndDescendantIds(Object.values(cardsByIdForIds || {}), sourceCardId).filter(id => id !== normalizeId(sourceCardId)) : [];
		return buildFilterFields({
			cards: [
				{ _id: sourceCardId || 'source-card', elements: sourceElements },
				...below.map(id => ({ _id: id, elements: getElementsForCard(id) || [] })),
			],
		});
	}, [sourceCardId, sourceElements, cardsByIdForIds, getElementsForCard]);

	// The lookups on the source card that point at this card or an ancestor.
	const targetCardIds = useMemo(() => targetCardChain(targetCard, collectionCards), [collectionCards, targetCard]);
	const linkOptions = useMemo(() => linkOptionsFor(sourceElements, targetCardIds), [sourceElements, targetCardIds]);

	// Exactly one link is the overwhelmingly common case (a registration has one
	// vehicle); picking it for the user removes a step that has no alternative.
	useEffect(() => {
		const names = linkOptions.map(option => option.value);
		setLinkElementName(prev => {
			if (prev && names.includes(prev)) return prev;
			return names.length === 1 ? names[0] : '';
		});
	}, [linkOptions]);

	const isCount = aggregate === 'count';

	// A link through a repeating group's entries (`<group>.<column>`, KPR-1055):
	// the total reads the entries of that group that name this item.
	const entryLink = useMemo(() => entryRefOf(linkElementName), [linkElementName]);
	const entryGroup = useMemo(() => (entryLink ? groupNamed(sourceElements, entryLink.group) : null), [entryLink, sourceElements]);
	const entryGroupLabel = entryGroup ? elementLabel(entryGroup) : '';

	const elementRows = useMemo(() => {
		// 'count' produces a number no matter what the chosen element holds, so
		// the matrix is asked about the AGGREGATE's output type, not the
		// element's.
		const effectiveTypeFor = dataType => (isCount ? 'number' : dataType);
		// With an entry link the group's own columns come first: each entry that
		// names this item gives its own value (`<group>.<column>`).
		const columns = entryGroup
			? columnsOf(entryGroup).filter(column => column?.name).map((column, index) => ({
				name: `${entryGroup.name}.${column.name}`,
				label: `${entryGroupLabel}${ENTRY_JOINER}${elementLabel(column)}`,
				dataType: column.dataType,
				options: column.options || {},
				system: false,
				column: true,
				order: index,
			}))
			: [];
		const own = sourceElements.map((el, index) => ({
			name: el?.name,
			label: el?.label?.singular || el?.name,
			dataType: el?.dataType,
			// The matrix reads a measurement's measure from here: mass may
			// drive mass, never length.
			options: el?.options || {},
			system: false,
			order: columns.length + index,
		})).filter(row => !!row.name);
		// System rows sort after every real element within their match class:
		// they are always available, so they must never crowd out the card's own
		// elements at the top of the list.
		const system = SYSTEM_ELEMENTS.map((row, index) => ({ ...row, order: columns.length + own.length + index }));
		return [...columns, ...own, ...system].map(row => {
			const match = entryGroup && PER_ENTRY_AGGREGATES.has(aggregate) && !row.column
				? { class: 'unmappable', reason: `${aggregateLabel(aggregate)} reads one value for each ${entryGroupLabel} entry, so pick a column of ${entryGroupLabel}.` }
				: NUMERIC_AGGREGATES.has(aggregate) && !NUMERIC_SOURCE_TYPES.has(row.dataType)
				? { class: 'unmappable', reason: `${aggregateLabel(aggregate)} needs a number or measurement element.` }
				: classify(effectiveTypeFor(row.dataType), targetType, {
					sourceOptions: row.options,
					targetOptions: targetOptions,
				});
			return { ...row, match };
		}).sort((a, b) => {
			const rank = MATCH_CLASS_META[a.match.class].rank - MATCH_CLASS_META[b.match.class].rank;
			return rank !== 0 ? rank : a.order - b.order;
		});
	}, [sourceElements, targetType, targetOptions, aggregate, isCount, entryGroup, entryGroupLabel]);

	// A count names no element: it stores the placeholder the catalog does
	// (`$createdAt`), or, reopened unedited, the name it was stored with.
	const effectiveSourceName = isCount && !elementRows.some(row => row.name === sourceElementName)
		? COUNT_SOURCE_ELEMENT
		: sourceElementName;

	const selectedRow = useMemo(
		() => elementRows.find(row => row.name === effectiveSourceName) || null,
		[elementRows, effectiveSourceName]
	);
	const match = selectedRow?.match || null;

	const fitRows = elementRows.filter(row => row.match.class !== 'unmappable');
	const unfitRows = elementRows.filter(row => row.match.class === 'unmappable');
	// A stored element that no longer fits is shown, so its state is not hidden.
	const unfitShown = showUnfit || (selectedRow?.match?.class === 'unmappable');

	// Keep the transform legal for the current pair: a single legal transform is
	// chosen for the user, and a stale one (element or aggregate changed under
	// it) is replaced rather than silently saved.
	useEffect(() => {
		const legal = match?.transforms || [];
		setTransformType(prev => {
			if (prev && legal.includes(prev)) return prev;
			const seeded = seededTransformRef.current;
			if (seeded && legal.includes(seeded)) return seeded;
			return legal.length ? legal[0] : '';
		});
	}, [match]);

	const orderByOptions = useMemo(() => {
		const dateColumns = entryGroup
			? columnsOf(entryGroup)
				.filter(column => DATE_TYPES.includes(column?.dataType) && column?.name)
				.map(column => ({ value: `${entryGroup.name}.${column.name}`, label: `${entryGroupLabel}${ENTRY_JOINER}${elementLabel(column)}` }))
			: [];
		const dateElements = sourceElements
			.filter(el => DATE_TYPES.includes(el?.dataType) && el?.name)
			.map(el => ({ value: el.name, label: el?.label?.singular || el.name }));
		return [
			...dateColumns,
			...dateElements,
			...SYSTEM_ELEMENTS.map(row => ({ value: row.name, label: row.label })),
		];
	}, [sourceElements, entryGroup, entryGroupLabel]);

	// The plan's default: order by the chosen element when it is a date, else by
	// the source item's creation time.
	const defaultOrderBy = useMemo(() => (
		selectedRow && DATE_TYPES.includes(selectedRow.dataType) ? selectedRow.name : '$createdAt'
	), [selectedRow]);

	// An order that names a column of ANOTHER group, or of no group now (the
	// link moved under it), is not kept: it falls back to the default rather
	// than being saved for the API to refuse.
	const orderByUsable = !orderBy || !entryRefOf(orderBy) || orderByOptions.some(option => option.value === orderBy);
	const chosenOrderBy = (orderByUsable && orderBy) || defaultOrderBy;

	const chosenTransform = transformType ? transformInfo(transformType) : null;

	const complete = !!(
		sourceCardId
		&& effectiveSourceName
		&& linkElementName
		&& match
		&& match.class !== 'unmappable'
		&& transformType
	);

	// A window measures from a date on the SOURCE card, or from one of the two
	// dates every item has.
	const windowFieldOptions = useMemo(() => ([
		...(sourceElements || [])
			.filter(el => el?.name && DATE_TYPES.includes(el.dataType))
			.map(el => ({ value: el.name, label: el.label?.singular || el.name })),
		{ value: 'createdAt', label: WINDOW_SYSTEM_LABELS.createdAt },
		{ value: 'updatedAt', label: WINDOW_SYSTEM_LABELS.updatedAt },
	]), [sourceElements]);

	// The config as the dialog would save it now: for the sentence underneath
	// and for Save.
	const draftConfig = useMemo(() => {
		if (!sourceCardId) return null;
		const config = {
			sourceCardId,
			sourceElementName: effectiveSourceName,
			linkElementName,
			aggregate,
			transform: { type: transformType },
		};
		// The API derives the same default; storing it would just be a second
		// copy of the rule to keep in sync.
		if (chosenOrderBy && chosenOrderBy !== defaultOrderBy) config.orderBy = chosenOrderBy;
		// Omitted when it says nothing: absent is how "all of them, ever"
		// reads, and storing an empty `where` would be a second spelling of
		// the same default.
		const where = {};
		if (windowField) where.window = { field: windowField, unit: windowUnit, every: Number(windowEvery) || 1 };
		// A stored filter goes back exactly as it was unless it was edited.
		const storedFilter = initialConfig?.where?.filter || '';
		if (whereFilter.trim()) where.filter = whereFilter === storedFilter ? storedFilter : whereFilter.trim();
		if (Object.keys(where).length) config.where = where;
		return config;
	}, [sourceCardId, effectiveSourceName, linkElementName, aggregate, transformType, chosenOrderBy, defaultOrderBy, windowField, windowUnit, windowEvery, whereFilter, initialConfig]);

	const linkLabel = linkLabelFor(linkOptions, linkElementName);
	const summary = useMemo(() => describeDrivenRule(draftConfig, {
		sourceCard,
		targetCard,
		sourceElements,
		fields: sourceIdFields,
		linkLabel,
	}), [draftConfig, sourceCard, targetCard, sourceElements, sourceIdFields, linkLabel]);

	// "Try it on" (KPR-959): the element as the dialog would save it, asked
	// about only once the rule is whole and its condition reads. A condition
	// the person edited is held to the same check Save holds it to.
	const draftElement = useMemo(() => (
		complete && draftConfig && element?.name
			? { ...element, options: { ...(element.options || {}), drivenFrom: draftConfig } }
			: null
	), [complete, draftConfig, element]);
	const draftProblem = useMemo(() => {
		const filter = draftConfig?.where?.filter || '';
		const storedFilter = (initialConfig?.where?.filter || '').trim();
		return filter && filter.trim() !== storedFilter ? drivenFilterProblem(filter) : null;
	}, [draftConfig, initialConfig]);

	const handleSave = () => {
		if (!complete || !draftConfig) return;
		// A filter typed as text is checked here, in words, before the card
		// ever reaches the server. One the person did not touch is not judged
		// again (the API does the same), so an old rule never blocks a save.
		const filter = draftConfig.where?.filter || '';
		const storedFilter = (initialConfig?.where?.filter || '').trim();
		if (filter && filter.trim() !== storedFilter) {
			const problem = drivenFilterProblem(filter);
			if (problem) {
				setWhereProblem(problem);
				return;
			}
		}
		onSave(draftConfig);
	};

	const pickElement = (row) => {
		if (row.match.class === 'unmappable') return;
		seededTransformRef.current = '';
		setSourceElementName(row.name);
	};

	const renderElementRow = (row) => {
		const meta = MATCH_CLASS_META[row.match.class];
		const disabled = row.match.class === 'unmappable';
		const selected = row.name === effectiveSourceName;
		return (
			<List.Item
				key={row.name}
				role='button'
				tabIndex={disabled ? -1 : 0}
				aria-disabled={disabled ? 'true' : undefined}
				aria-pressed={selected}
				className={[
					'driven-from-modal__element',
					selected ? 'driven-from-modal__element--selected' : '',
					disabled ? 'driven-from-modal__element--unfit' : '',
				].filter(Boolean).join(' ')}
				onClick={disabled ? undefined : () => pickElement(row)}
				onKeyDown={disabled ? undefined : (event) => {
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						pickElement(row);
					}
				}}
			>
				<div className='driven-from-modal__element-row'>
					<span className='driven-from-modal__element-name'>
						<DataTypeBadge dataType={row.dataType} iconOnly className='data-type-badge--bare' />
						<span className='driven-from-modal__element-label'>{row.label}</span>
					</span>
					{meta.tag ? <Tag className='driven-from-modal__element-tag'>{meta.tag}</Tag> : null}
				</div>
				{disabled && row.match.reason ? (
					<div className='driven-from-modal__element-reason'>{row.match.reason}</div>
				) : null}
			</List.Item>
		);
	};

	const handleSourceCard = (value) => {
		const next = normalizeId(value) || '';
		if (next === sourceCardId) return;
		setSourceCardId(next);
		setSourceElementName(isCount ? COUNT_SOURCE_ELEMENT : '');
		setOrderBy('');
		// The filter names the old card's elements by id, and a window may
		// measure from one of them: neither means anything on another card.
		setWhereFilter('');
		setWhereProblem(null);
		setWindowField(prev => (WINDOW_SYSTEM_LABELS[prev] ? prev : (prev ? 'createdAt' : '')));
		setShowUnfit(false);
		seededTransformRef.current = '';
	};

	const handleAggregate = (value) => {
		seededTransformRef.current = '';
		if (value === 'count') setSourceElementName(COUNT_SOURCE_ELEMENT);
		// From a count to an aggregate that reads an element: ask which.
		else if (isCount) setSourceElementName('');
		setAggregate(value);
		setShowUnfit(false);
	};

	return (
		<Modal
			open={open}
			onCancel={onCancel}
			onOk={handleSave}
			okText='Save'
			okButtonProps={{ disabled: !complete }}
			title={`Driven from — ${element?.label?.singular || 'element'}`}
			width={720}
			destroyOnClose
			className='driven-from-modal'
		>
			<Space direction='vertical' size={16} className='w-100 driven-from-modal__body' style={{ width: '100%' }}>

				<Typography.Text type='secondary'>
					This element’s value is kept up to date from the items of another card that link
					to this one. Nobody fills it in by hand.
				</Typography.Text>

				<InputSelect
					elementName='drivenFromSourceCard'
					elementLabel='From'
					value={sourceCardId || undefined}
					onChange={(name, value) => handleSourceCard(value)}
					options={cardOptions}
					placeholder='Choose the card whose items feed this element'
					allowClear={false}
				/>

				{sourceCardId && !linkOptions.length ? (
					<Alert
						type='warning'
						showIcon
						message='No link between these cards yet'
						description={`Add an item lookup element pointing at ${targetCardName} to ${sourceCardName} first. That lookup is what tells keepr which ${targetCardName} item a ${sourceCardName} item belongs to.`}
					/>
				) : null}

				{sourceCardId && linkOptions.length > 1 ? (
					<InputSelect
						elementName='drivenFromLinkElement'
						elementLabel='Linked by'
						value={linkElementName || undefined}
						onChange={(name, value) => setLinkElementName(value || '')}
						options={linkOptions}
						placeholder={`Which lookup points at ${targetCardName}?`}
						allowClear={false}
					/>
				) : null}

				{sourceCardId && linkOptions.length === 1 ? (
					<Typography.Text type='secondary'>
						Linked by <strong>{linkOptions[0].label}</strong>, the only lookup on {sourceCardName} pointing at {targetCardName}.
					</Typography.Text>
				) : null}

				{sourceCardId ? (<>
					<div>
						<label htmlFor='drivenFromAggregate' className='d-block'>Take</label>
						<Select
							id='drivenFromAggregate'
							size='large'
							className='w-100'
							style={{ width: '100%' }}
							value={aggregate}
							onChange={handleAggregate}
							options={entryLink ? ENTRY_AGGREGATE_OPTIONS : AGGREGATE_OPTIONS}
						/>
						{isCount && match && match.class === 'unmappable' ? (
							<Typography.Text type='danger' className='d-block'>
								{`A count is a number, and ${element?.label?.singular || 'this element'} can’t hold one. ${match.reason || ''}`.trim()}
							</Typography.Text>
						) : null}
					</div>

					{isCount ? null : (
						<div>
							<Typography.Text strong className='d-block' id='drivenFromElementLabel'>Of the element</Typography.Text>
							{entryGroup ? (
								<Typography.Text type='secondary' className='d-block'>
									{PER_ENTRY_AGGREGATES.has(aggregate)
										? `Pick a column of ${entryGroupLabel}: each entry that names this ${targetCardName} gives its own value.`
										: `Pick a column of ${entryGroupLabel}, or an element of the ${sourceCardName} item that holds the entry.`}
								</Typography.Text>
							) : null}
							{fitRows.length ? (
								<List
									size='small'
									bordered
									className='driven-from-modal__elements'
									aria-labelledby='drivenFromElementLabel'
									dataSource={unfitShown ? [...fitRows, ...unfitRows] : fitRows}
									renderItem={renderElementRow}
								/>
							) : (
								<>
									<Typography.Text type='secondary' className='d-block'>
										{`No element on ${sourceCardName} fits ${aggregateLabel(aggregate).toLowerCase()}.`}
									</Typography.Text>
									{unfitShown ? (
										<List
											size='small'
											bordered
											className='driven-from-modal__elements'
											aria-labelledby='drivenFromElementLabel'
											dataSource={unfitRows}
											renderItem={renderElementRow}
										/>
									) : null}
								</>
							)}
							{unfitRows.length ? (
								<Button
									type='link'
									size='small'
									className='driven-from-modal__unfit-toggle'
									aria-expanded={unfitShown}
									onClick={() => setShowUnfit(!unfitShown)}
								>
									{unfitShown
										? `Hide the ${unfitRows.length} that don’t fit`
										: `Show ${unfitRows.length} that don’t fit`}
								</Button>
							) : null}
						</div>
					)}

					<div className='driven-from-modal__where'>
						<Typography.Text strong className='d-block'>Only those where</Typography.Text>
						<DrivenWhereEditor
							value={whereFilter}
							onChange={(next) => { setWhereFilter(next); setWhereProblem(null); }}
							elements={sourceElements}
							idFields={sourceIdFields}
							collectionId={collectionId}
							problem={whereProblem}
						/>
						{entryGroup ? (
							<Typography.Text type='secondary' className='d-block'>
								A condition picks which {sourceCardName} items count, not which {entryGroupLabel} entries.
							</Typography.Text>
						) : null}
					</div>

					<div className='driven-from-modal__window'>
						<label htmlFor='drivenFromWindow' className='d-block'>Over</label>
						<div className='driven-from-modal__window-line'>
							<Select
								id='drivenFromWindow'
								className='driven-from-modal__window-span'
								value={windowField ? 'last' : 'all'}
								options={WINDOW_SPAN_OPTIONS}
								onChange={(value) => setWindowField(value === 'last' ? (windowField || 'createdAt') : '')}
							/>
							{windowField ? (<>
								<InputNumber
									min={1}
									max={400}
									precision={0}
									value={windowEvery}
									aria-label='How many'
									className='driven-from-modal__window-every'
									onChange={value => setWindowEvery(value || 1)}
								/>
								<Select
									className='driven-from-modal__window-unit'
									value={windowUnit}
									aria-label='Unit'
									options={WINDOW_UNIT_OPTIONS}
									onChange={setWindowUnit}
								/>
								<span className='driven-from-modal__window-by'>by</span>
								<Select
									id='drivenFromWindowField'
									className='driven-from-modal__window-field'
									value={windowField}
									aria-label='Measured by'
									options={windowFieldOptions}
									onChange={value => setWindowField(value || 'createdAt')}
								/>
							</>) : null}
						</div>
						{windowField ? (
							<Typography.Text type='secondary' className='d-block'>
								This value changes with the clock alone: keepr refreshes it every day, without anyone
								editing an item.
							</Typography.Text>
						) : null}
					</div>
				</>) : null}

				{match && match.class === 'coercible' && (match.transforms || []).length > 1 ? (
					<div>
						<label htmlFor='drivenFromTransform' className='d-block'>Conversion</label>
						<Select
							id='drivenFromTransform'
							size='large'
							className='w-100'
							style={{ width: '100%' }}
							value={transformType || undefined}
							onChange={value => setTransformType(value)}
							options={(match.transforms || []).map(name => ({
								value: name,
								label: TRANSFORM_LABELS[name] || name,
							}))}
						/>
					</div>
				) : null}

				{chosenTransform?.lossy ? (
					<Alert type='warning' showIcon message={chosenTransform.warning} />
				) : null}

				{effectiveSourceName && aggregate === 'latest' ? (
					<Collapse
						size='small'
						items={[{
							key: 'advanced',
							label: 'Advanced',
							children: (
								<div>
									<label htmlFor='drivenFromOrderBy' className='d-block'>Order by</label>
									<Select
										id='drivenFromOrderBy'
										size='large'
										className='w-100'
										style={{ width: '100%' }}
										value={chosenOrderBy}
										onChange={value => setOrderBy(value)}
										options={orderByOptions}
									/>
									<Typography.Text type='secondary'>
										{entryGroup
											? `Which entry counts as the latest one: a date column of ${entryGroupLabel}, or a date on the ${sourceCardName} item.`
											: 'Which item counts as the latest one.'}
									</Typography.Text>
								</div>
							),
						}]}
					/>
				) : null}

				{complete ? (
					<Alert
						type='warning'
						showIcon
						message={`Anyone who can edit ${sourceCardName} items can change this element on ${targetCardName} items, including items they can’t edit directly.`}
					/>
				) : null}

				{summary ? (
					<p className='driven-from-modal__summary' aria-live='polite'>{summary}</p>
				) : null}

				{sourceCardId ? (
					<DrivenTryIt
						cardId={normalizeId(targetCard?._id)}
						collectionId={collectionId}
						element={draftElement}
						aggregate={aggregate}
						entries={!!entryLink}
						ready={!!draftElement && !draftProblem}
						notReady={draftProblem || 'Finish the rule to try it on an item.'}
					/>
				) : null}

			</Space>
		</Modal>
	);
}
